import React, { useState, useEffect, useCallback } from 'react';
import {
  FiSettings,
  FiSave,
  FiRefreshCw,
  FiClock,
  FiShuffle,
  FiTarget,
  FiEye,
  FiMinus,
  FiCheck,
  FiAlertCircle,
  FiLoader,
  FiPause,
  FiList,
  FiGrid,
  FiLayers
} from 'react-icons/fi';
import { examSettingsAPI } from '../../services/api';

const SHOW_RESULTS_OPTIONS = [
  { value: 'immediately', label: 'Immediately after each question' },
  { value: 'after_submission', label: 'After submission' },
  { value: 'scheduled', label: 'At a scheduled time' },
  { value: 'never', label: 'Never' }
];

const VIEW_MODES = [
  { value: 'list', label: 'List', icon: FiList, description: 'Vertical list' },
  { value: 'tile', label: 'Tiles', icon: FiGrid, description: 'Card grid' },
  { value: 'table', label: 'Table', icon: FiLayers, description: 'Compact table' }
];

const STORAGE_VIEW_KEY = 'exam-settings-view-mode';

const defaultConfig = {
  duration: 60,
  totalQuestions: 50,
  passingScore: 50,
  shuffleQuestions: true,
  shuffleOptions: true,
  showResults: 'after_submission',
  allowReview: true,
  allowPauseResume: false,
  maxAttempts: 1,
  timePerQuestion: 0,
  negativeMarking: { enabled: false, penalty: 0.25 }
};

const ExamSettings = () => {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [config, setConfig] = useState({ ...defaultConfig });
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_VIEW_KEY);
      if (VIEW_MODES.some(m => m.value === saved)) return saved;
    } catch (_) {}
    return 'list';
  });

  const setViewModeAndPersist = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(STORAGE_VIEW_KEY, mode);
    } catch (_) {}
  };

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const res = await examSettingsAPI.list();
      const data = res?.data?.data || [];
      setRows(data);
    } catch (err) {
      setError('Failed to load exam settings: ' + (err?.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleSelect = (row) => {
    setSelected(row);
    const c = row.config ? { ...defaultConfig, ...row.config } : { ...defaultConfig };
    if (c.negativeMarking && typeof c.negativeMarking === 'object') {
      // keep as-is
    } else {
      c.negativeMarking = { enabled: false, penalty: 0.25 };
    }
    setConfig(c);
    setError(null);
    setSuccess(null);
  };

  const handleConfigChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleNegativeMarkingChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      negativeMarking: { ...(prev.negativeMarking || {}), [field]: value }
    }));
  };

  const handleSave = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      setError(null);
      await examSettingsAPI.save({
        examType: selected.examType,
        class: selected.class,
        subjectName: selected.subjectName,
        config
      });
      const res = await examSettingsAPI.list();
      setRows(res?.data?.data || []);
      setSelected((prev) => (prev ? { ...prev, config } : null));
      setSuccess('Exam settings saved.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save: ' + (err?.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <div className="text-center">
          <FiLoader className="h-10 w-10 animate-spin text-slate-400 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading exam settings…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Exam settings</h1>
          <p className="text-sm text-slate-500 mt-0.5">Set default rules by exam type, class and subject.</p>
        </div>

        {error && (
          <div className="mb-6 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <FiAlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="mb-6 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <FiCheck className="h-4 w-4 shrink-0" />
            <span>{success}</span>
          </div>
        )}

        {/* <div className="grid grid-cols-1 lg:grid-cols-[350px_1fr] gap-6"> */}
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50/80">
                <h2 className="text-sm font-medium text-slate-800">Select exam</h2>
                <div className="flex rounded-lg border border-slate-200 bg-white p-0.5" role="group" aria-label="View">
                  {VIEW_MODES.map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === value ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                      onClick={() => setViewModeAndPersist(value)}
                      title={VIEW_MODES.find(m => m.value === value)?.description}
                      aria-pressed={viewMode === value}
                    >
                      <Icon className="h-3.5 w-3" aria-hidden />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {rows.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-sm text-slate-500 mb-3">No subjects in question bank. Add subjects and questions first.</p>
                  <button type="button" onClick={fetchList} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">
                    <FiRefreshCw className="h-4 w-4" /> Refresh
                  </button>
                </div>
              ) : viewMode === 'table' ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/80">
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Subject</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Exam type</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Class</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Questions</th>
                        <th className="px-4 py-3 w-0" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {rows.map((row) => {
                        const isSelected = selected && selected.examType === row.examType && selected.class === row.class && selected.subjectName === row.subjectName;
                        return (
                          <tr
                            key={`${row.examType}-${row.class}-${row.subjectName}`}
                            className={`cursor-pointer transition-colors ${isSelected ? 'bg-slate-100' : 'hover:bg-slate-50/50'}`}
                            onClick={() => handleSelect(row)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSelect(row)}
                            role="button"
                            tabIndex={0}
                            aria-label={`Select ${row.subjectName}, ${row.examType}, ${row.class}`}
                          >
                            <td className="px-4 py-3 font-medium text-slate-900">{row.subjectName}</td>
                            <td className="px-4 py-3 text-slate-600">{row.examType}</td>
                            <td className="px-4 py-3 text-slate-600">{row.class}</td>
                            <td className="px-4 py-3 text-slate-600">{row.questionCount}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-medium ${isSelected ? 'text-slate-700' : 'text-slate-500'}`}>
                                {isSelected ? 'Selected' : 'Select'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={`p-3 flex flex-col gap-2 ${viewMode === 'tile' ? 'sm:grid sm:grid-cols-2' : ''}`}>
                  {rows.map((row) => {
                    const isSelected = selected && selected.examType === row.examType && selected.class === row.class && selected.subjectName === row.subjectName;
                    return (
                      <div
                        key={`${row.examType}-${row.class}-${row.subjectName}`}
                        className={`rounded-lg border p-3 cursor-pointer transition-colors ${isSelected ? 'border-slate-800 bg-slate-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/50'}`}
                        onClick={() => handleSelect(row)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleSelect(row)}
                      >
                        <div className="font-medium text-slate-900">{row.subjectName}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{row.examType} · {row.class}</div>
                        <div className="text-xs text-slate-400 mt-1">{row.questionCount} in bank</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          {/* </div> */}

          {selected && (
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6">
              <h2 className="text-base font-semibold text-slate-900 mb-4">
                {selected.subjectName} <span className="text-slate-500 font-normal">({selected.examType}, {selected.class})</span>
              </h2>

              <div className="space-y-6">
                <section>
                  <h3 className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3 pb-2 border-b border-slate-100">
                    <FiList className="h-4 w-4 text-slate-500" /> Questions & time
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Number of questions</label>
                      <input type="number" min={1} max={200} value={config.totalQuestions} onChange={(e) => handleConfigChange('totalQuestions', parseInt(e.target.value, 10) || 1)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" />
                      <p className="text-xs text-slate-500 mt-1">Max in bank: {selected.questionCount}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Time limit (min)</label>
                      <input type="number" min={1} value={config.duration} onChange={(e) => handleConfigChange('duration', parseInt(e.target.value, 10) || 60)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Time per question (s, 0 = none)</label>
                      <input type="number" min={0} value={config.timePerQuestion ?? 0} onChange={(e) => handleConfigChange('timePerQuestion', parseInt(e.target.value, 10) || 0)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" />
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3 pb-2 border-b border-slate-100">
                    <FiShuffle className="h-4 w-4 text-slate-500" /> Shuffling
                  </h3>
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                      <input type="checkbox" checked={config.shuffleQuestions !== false} onChange={(e) => handleConfigChange('shuffleQuestions', e.target.checked)} className="rounded border-slate-300 text-slate-700 focus:ring-slate-400" />
                      Shuffle questions
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                      <input type="checkbox" checked={config.shuffleOptions !== false} onChange={(e) => handleConfigChange('shuffleOptions', e.target.checked)} className="rounded border-slate-300 text-slate-700 focus:ring-slate-400" />
                      Shuffle options
                    </label>
                  </div>
                </section>

                <section>
                  <h3 className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3 pb-2 border-b border-slate-100">
                    <FiPause className="h-4 w-4 text-slate-500" /> Pause & attempts
                  </h3>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                      <input type="checkbox" checked={config.allowPauseResume === true} onChange={(e) => handleConfigChange('allowPauseResume', e.target.checked)} className="rounded border-slate-300 text-slate-700 focus:ring-slate-400" />
                      Allow pause and resume
                    </label>
                    <div className="max-w-[120px]">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Max attempts (0 = unlimited)</label>
                      <input type="number" min={0} value={config.maxAttempts ?? 1} onChange={(e) => handleConfigChange('maxAttempts', parseInt(e.target.value, 10) >= 0 ? parseInt(e.target.value, 10) : 1)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" />
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3 pb-2 border-b border-slate-100">
                    <FiTarget className="h-4 w-4 text-slate-500" /> Scoring
                  </h3>
                  <div className="space-y-3">
                    <div className="max-w-[120px]">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Passing score (%)</label>
                      <input type="number" min={0} max={100} value={config.passingScore ?? 50} onChange={(e) => handleConfigChange('passingScore', parseInt(e.target.value, 10) || 0)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                        <input type="checkbox" checked={config.negativeMarking?.enabled === true} onChange={(e) => handleNegativeMarkingChange('enabled', e.target.checked)} className="rounded border-slate-300 text-slate-700 focus:ring-slate-400" />
                        Negative marking
                      </label>
                      {config.negativeMarking?.enabled && (
                        <div className="mt-2 pl-4 border-l-2 border-slate-200">
                          <label className="block text-xs font-medium text-slate-600 mb-1">Penalty (e.g. 0.25)</label>
                          <input type="number" min={0} max={1} step={0.05} value={config.negativeMarking?.penalty ?? 0.25} onChange={(e) => handleNegativeMarkingChange('penalty', parseFloat(e.target.value) || 0)} className="w-full max-w-[100px] px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400" />
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3 pb-2 border-b border-slate-100">
                    <FiEye className="h-4 w-4 text-slate-500" /> Results & review
                  </h3>
                  <div className="space-y-3">
                    <div className="max-w-xs">
                      <label className="block text-sm font-medium text-slate-700 mb-1">When to show results</label>
                      <select value={config.showResults || 'after_submission'} onChange={(e) => handleConfigChange('showResults', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
                        {SHOW_RESULTS_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                      <input type="checkbox" checked={config.allowReview !== false} onChange={(e) => handleConfigChange('allowReview', e.target.checked)} className="rounded border-slate-300 text-slate-700 focus:ring-slate-400" />
                      Allow review after submission
                    </label>
                  </div>
                </section>

                <div className="pt-4 border-t border-slate-200">
                  <button type="button" onClick={handleSave} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed">
                    {saving ? <><FiLoader className="h-4 w-4 animate-spin" /> Saving…</> : <><FiSave className="h-4 w-4" /> Save exam settings</>}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamSettings;
